import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import ServiceTile from "@/components/ServiceTile";
import TestimonialCard from "@/components/TestimonialCard";
import ConsentsCounter from "@/components/ConsentsCounter";
import GoogleReviewsBadge from "@/components/GoogleReviewsBadge";
import FeaturedProjectsCarousel from "@/components/FeaturedProjectsCarousel";
import SEO, { ORGANISATION_SCHEMA, SITE_URL } from "@/components/SEO";
import { API } from "@/lib/apiBase";

// Custom display order — "Solutions for Architects & Industry Professionals"
// is positioned immediately after Heritage.
const SERVICE_ORDER = [
  "residential",
  "commercial",
  "rural",
  "heritage",
  "other-services",
  "subdivision",
  "terraced-housing",
  "coastal",
  "feasibility",
  "crosslease-to-freehold",
  "childcare-healthcare",
];

export default function HomePage() {
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed`);
        const [servicesRes, testimonialsRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/testimonials`),
        ]);
        // Use the admin-configurable `tile_order` if any service has one set;
        // otherwise fall back to the hard-coded SERVICE_ORDER constant.
        const hasOrder = servicesRes.data.some((s) => typeof s.tile_order === "number");
        const ordered = hasOrder
          ? [...servicesRes.data].sort(
              (a, b) => (a.tile_order ?? 999) - (b.tile_order ?? 999)
            )
          : SERVICE_ORDER
              .map((slug) => servicesRes.data.find((s) => s.slug === slug))
              .filter(Boolean);
        setServices(ordered);
        setTestimonials(testimonialsRes.data.slice(0, 3));
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Bento grid (12 tiles, 4 cols, 4 rows = 16 cells):
  // Row1-2: Res(2x2) + Com + Rural + Heritage + Architects (8 cells)
  // Row3:   Subdivision(2x) + Terraced + Coastal (4 cells)
  // Row4:   Feasibility + Crosslease + Childcare + Site&Cliff (4 cells)
  const bentoClasses = [
    "col-span-1 md:col-span-2 row-span-2 min-h-[400px]", // 0 Residential
    "col-span-1 min-h-[200px]", // 1 Commercial
    "col-span-1 min-h-[200px]", // 2 Rural
    "col-span-1 min-h-[200px]", // 3 Heritage
    "col-span-1 min-h-[200px]", // 4 Architects & Industry Professionals
    "col-span-1 md:col-span-2 min-h-[250px]", // 5 Subdivision wide
    "col-span-1 min-h-[200px]", // 6 Terraced Housing
    "col-span-1 min-h-[200px]", // 7 Coastal
    "col-span-1 min-h-[200px]", // 8 Feasibility & Scoping
    "col-span-1 min-h-[200px]", // 9 Crosslease
    "col-span-1 min-h-[200px]", // 10 Childcare
    "col-span-1 min-h-[200px]", // 11 Site & Cliff Stabilisation
  ];

  return (
    <div className="page-wrapper" data-testid="home-page">
      <SEO
        title="Resource Consent Auckland | Planner Auckland | Code Planning"
        description="Resource consent planners in Auckland. Senior-led team handling residential, subdivision, terraced housing, heritage, commercial, coastal & cross lease consents across Auckland — Free Property Planning Review on every enquiry."
        path="/"
        keywords="resource consent auckland, planner auckland, planning consultant auckland, town planner auckland, resource consent planner, cross lease conversion auckland, subdivision consent auckland, code planning, auckland north shore planner"
        jsonLd={[
          ORGANISATION_SCHEMA,
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "@id": `${SITE_URL}/#website`,
            url: SITE_URL,
            name: "Code Planning Limited",
            description: "Resource Consents, Simplified.",
            publisher: { "@id": `${SITE_URL}/#organization` },
            potentialAction: {
              "@type": "SearchAction",
              target: `${SITE_URL}/blog?q={search_term_string}`,
              "query-input": "required name=search_term_string",
            },
          },
        ]}
      />
      {/* Hero Section */}
      <section className="flex flex-col justify-center relative bg-ink pt-6 md:pt-8 pb-8 md:pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl">
            {/* HERO EYEBROW — locked text per client. DO NOT change to
                "Our Expertise" or "Senior Planning Consultancy" without
                explicit client approval. */}
            <p
              className="text-sm uppercase tracking-widest text-gray-400 mb-6 animate-slide-up"
              data-testid="hero-eyebrow"
            >
              Your local planner
            </p>
            <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-none mb-6 animate-slide-up-delay-1">
              Resource Consents.
              <br />
              <span className="italic text-gray-400">Simplified.</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-400 leading-relaxed max-w-2xl mb-8 animate-slide-up-delay-2">
              A small, senior planning team based on Auckland's North Shore — bringing big-firm expertise
              and the focus of a senior, hands-on team on every project.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-slide-up-delay-2">
              <div className="flex flex-col gap-3">
                <p className="text-gray-300 text-sm md:text-base leading-relaxed max-w-xl">
                  <span className="text-white font-semibold">Free Property Planning Review</span> — send us the property address and a brief outline of what you're proposing. One of our planners will undertake an initial desktop review of the planning constraints and likely consent pathway.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/contact#property-form"
                    className="bg-white text-ink px-8 py-4 uppercase tracking-widest text-sm font-bold hover:bg-gray-200 transition-colors duration-200 inline-block text-center"
                    data-testid="hero-cta"
                  >
                    Check My Property
                  </Link>
                  <Link
                    to="/services"
                    className="border border-white text-white px-8 py-4 uppercase tracking-widest text-sm font-bold hover:bg-white hover:text-ink transition-colors duration-200 inline-block text-center"
                    data-testid="hero-services-link"
                  >
                    Our Services
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid Section */}
      <section id="services" className="pt-4 md:pt-6 pb-12 md:pb-16 bg-ink">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 md:mb-10">
            <div>
              <p className="text-sm uppercase tracking-widest text-gray-400 mb-4">
                What We Do
              </p>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white">
                Resource Consents<span className="italic text-gray-400">. Simplified.</span>
              </h2>
            </div>
            <Link
              to="/services"
              className="flex items-center gap-2 text-white hover:gap-4 transition-all duration-200 mt-4 md:mt-0"
              data-testid="view-all-services"
            >
              <span className="text-sm uppercase tracking-widest font-medium">
                View All
              </span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="bg-neutral-800 animate-pulse min-h-[200px]"
                />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {services.map((service, index) => (
                <ServiceTile
                  key={service.id}
                  service={service}
                  className={bentoClasses[index] || "col-span-1 min-h-[200px]"}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 md:py-16 bg-ink text-white border-t border-neutral-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 text-center">
            <div>
              <p className="font-heading text-6xl md:text-7xl font-bold mb-2">40+</p>
              <p className="text-sm uppercase tracking-widest text-gray-400">
                Years Combined Experience
              </p>
            </div>
            <div data-testid="consents-counter-block">
              <p className="font-heading text-6xl md:text-7xl font-bold mb-2">
                <ConsentsCounter />
              </p>
              <p className="text-sm uppercase tracking-widest text-gray-400">
                Successful Resource Consents
              </p>
              <p className="text-xs uppercase tracking-widest text-gray-500 mt-1">
                & counting
              </p>
            </div>
            <div>
              <p className="font-heading text-6xl md:text-7xl font-bold mb-2">99%</p>
              <p className="text-sm uppercase tracking-widest text-gray-400">
                Success Rate
              </p>
            </div>
          </div>
          <p className="text-center text-gray-400 italic mt-8 md:mt-10 max-w-2xl mx-auto text-lg" data-testid="counter-tagline">
            Each one a project we navigated personally — not outsourced, not automated.
          </p>
          <p className="text-center text-gray-500 mt-4 max-w-2xl mx-auto text-sm uppercase tracking-widest" data-testid="aotearoa-line">
            Working with clients all over Aotearoa.
          </p>
        </div>
      </section>

      {/* Featured Projects Carousel */}
      <FeaturedProjectsCarousel services={services} />

      {/* Non-Notified Specialty Section */}
      <section className="py-14 md:py-20 bg-neutral-950 border-t border-neutral-900" data-testid="non-notified-callout">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <p className="text-sm uppercase tracking-widest text-gray-500 mb-4">
            How we work
          </p>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-6 leading-tight">
            We specialise in delivering
            <br />
            <span className="text-white">non-notified consents.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 text-gray-300 text-base md:text-lg leading-relaxed">
            <p>
              The notification process is costly, time-consuming, and full of
              uncertainty. Once a consent is publicly or limited notified, you
              lose control of the programme, the cost, and the outcome.
            </p>
            <p>
              <span className="text-white font-semibold">
                99.9% of our applications are approved on a non-notified basis.
              </span>{" "}
              We achieve that by giving honest, upfront advice on exactly what
              council requires, then working with the design team to deliver
              it. It is our job to tell you what you{" "}
              <em>need</em> to hear — not what you want to hear.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Preview */}
      <section className="py-12 md:py-16 bg-neutral-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 md:mb-10 gap-6">
            <div>
              <p className="text-sm uppercase tracking-widest text-gray-400 mb-4">
                Client Stories
              </p>
              <h2 className="font-heading text-4xl md:text-5xl font-semibold text-white mb-6">
                5.0 <span className="text-yellow-400">★</span> on Google
                <br />
                <span className="italic text-gray-300 text-3xl md:text-4xl">80+ client reviews</span>
              </h2>
              <GoogleReviewsBadge />
            </div>
            <Link
              to="/testimonials"
              className="flex items-center gap-2 text-white hover:gap-4 transition-all duration-200 mt-4 md:mt-0"
              data-testid="view-all-testimonials"
            >
              <span className="text-sm uppercase tracking-widest font-medium">
                All Reviews
              </span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <TestimonialCard key={testimonial.id} testimonial={testimonial} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16 bg-ink">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-6">
            Ready to start your project?
          </h2>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-10">
            Request a Free Property Planning Review — send us the address and a short outline, and one of our planners will assess your site.
          </p>
          <Link
            to="/contact#property-form"
            className="bg-white text-ink px-8 py-4 uppercase tracking-widest text-sm font-bold hover:bg-gray-200 transition-colors duration-200 inline-block"
            data-testid="cta-contact"
          >
            Check My Property
          </Link>
        </div>
      </section>
    </div>
  );
}
